import { push } from 'redux-first-history';
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { addToRootSaga } from 'store';

import {
  createIntegration,
  createMobileApp,
  deleteIntegration,
  deleteMobileApp,
  getIntegrations,
  getMobileAppInfo,
  getMobileApps,
  sendTestPush,
  updateIntegration,
  updateMobileApp,
} from 'api/MobileAppsAPI';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import * as actionTypes from './actionTypes';
import {
  assignIntegrationError,
  assignIntegrationResponse,
  createIntegrationError,
  createIntegrationResponse,
  deleteIntegrationError,
  deleteIntegrationResponse,
  deleteMobileAppResponse,
  fetchIntegrationsError,
  fetchIntegrationsResponse,
  fetchMobileAppError,
  fetchMobileAppResponse,
  fetchMobileAppsResponse,
  sendTestPushError,
  sendTestPushResponse,
  updateIntegrationError,
  updateIntegrationResponse,
} from './actions';
import { TOAST_ACTIONS } from './constants';
import { Integration, MobileApp } from './types';
import { extractErrorMessage, groupIntegrations } from './utils';

export function* fetchMobileApps() {
  try {
    const { data } = yield call(getMobileApps);
    const mobileApps = data.results as MobileApp[];
    const mobileAppsById = mobileApps.reduce((acc: Record<number, MobileApp>, app) => {
      acc[app.id] = app;
      return acc;
    }, {});
    yield put(fetchMobileAppsResponse(mobileAppsById));
  } catch (error) {
    yield put(
      addNegativeToastWithErrorId(
        'Unable to fetch the list of mobile apps.',
        TOAST_ACTIONS.fetchMobileApps,
        error,
      ),
    );
  }
}

export function* fetchMobileAppInfo({ id }: { id: number }) {
  try {
    const { data } = yield call(getMobileAppInfo, id);
    yield put(fetchMobileAppResponse(data.results));
  } catch (error: any) {
    yield put(
      addNegativeToastWithErrorId(
        'Unable to fetch the mobile app.',
        TOAST_ACTIONS.fetchMobileAppInfo,
        error,
      ),
    );
    yield put(fetchMobileAppError(error.toString()));
  }
}

export function* createMobileAppRequest({ mobileApp }: { mobileApp: MobileApp }) {
  try {
    yield call(createMobileApp, mobileApp);
    const { data } = yield call(getMobileApps);
    const createdApp: MobileApp = data.results.find(
      (app: { name: string; packageName: string }) =>
        app.name === mobileApp.name && app.packageName === mobileApp.packageName,
    );
    yield put(addPositiveToast('Created new mobile app'));
    yield put(push(`/integrations/mobileApps/${createdApp.id}`));
  } catch (error) {
    yield put(
      addNegativeToastWithErrorId(
        'Something went wrong when trying to create the app. Please try again.',
        TOAST_ACTIONS.createMobileApp,
        error,
      ),
    );
  }
}

export function* updateMobileAppRequest({ mobileApp }: { mobileApp: MobileApp }) {
  try {
    yield call(updateMobileApp, mobileApp);
    yield put(fetchMobileAppResponse(mobileApp));
    yield put(push(`/integrations/mobileApps/${mobileApp.id}`));
    yield put(addPositiveToast('Updated mobile app'));
  } catch (error) {
    yield put(
      addNegativeToastWithErrorId(
        'Something went wrong when trying to update the app. Please try again.',
        TOAST_ACTIONS.updateMobileApp,
        error,
      ),
    );
  }
}

export function* deleteMobileAppRequest({ mobileApp }: { mobileApp: MobileApp }) {
  try {
    yield call(deleteMobileApp, mobileApp);
    yield put(deleteMobileAppResponse());
    yield put(push(`/integrations/mobileApps`));
    yield put(addPositiveToast('Deleted the mobile app'));
  } catch (error) {
    yield put(
      addNegativeToastWithErrorId(
        'Something went wrong when trying to delete the app. Please try again.',
        TOAST_ACTIONS.deleteMobileApp,
        error,
      ),
    );
  }
}

export function* fetchIntegrationsRequest() {
  try {
    const { data } = yield call(getIntegrations);
    const groupedIntegrations = groupIntegrations(data.results);
    yield put(fetchIntegrationsResponse(groupedIntegrations));
  } catch (error) {
    yield put(fetchIntegrationsError());
    if ((error as any)?.response?.status === 403) return;
    yield put(
      addNegativeToastWithErrorId(
        'Unable to fetch the list of push integrations. Please try again.',
        TOAST_ACTIONS.fetchIntegrations,
        error,
      ),
    );
  }
}

export function* createIntegrationRequest({ integration }: { integration: Integration }) {
  try {
    yield call(createIntegration, integration);
    const { data } = yield call(getIntegrations);
    const groupedIntegrations = groupIntegrations(data.results);
    yield put(createIntegrationResponse(groupedIntegrations));
    yield put(addPositiveToast('Created the push credential.'));
  } catch (error: any) {
    yield put(createIntegrationError());
    const msg = extractErrorMessage(error);
    yield put(addNegativeToastWithErrorId(msg, TOAST_ACTIONS.createIntegration, error));
  } finally {
    const response: { data?: any } = yield call(getIntegrations);
    const groupedIntegrations = groupIntegrations(response?.data?.results);
    yield put(createIntegrationResponse(groupedIntegrations));
  }
}

export function* assignIntegrationRequest({ integration }: { integration: Integration }) {
  try {
    yield call(updateIntegration, integration);
    const { data } = yield call(getIntegrations);
    const groupedIntegrations = groupIntegrations(data.results);
    yield put(assignIntegrationResponse(groupedIntegrations));
    yield put(addPositiveToast('Assigned the integration succesfully'));
  } catch (error: any) {
    yield put(assignIntegrationError());
    const msg = extractErrorMessage(error);
    yield put(addNegativeToastWithErrorId(msg, TOAST_ACTIONS.assignIntegration, error));
  } finally {
    const response: { data?: any } = yield call(getIntegrations);
    const groupedIntegrations = groupIntegrations(response?.data?.results);
    yield put(fetchIntegrationsResponse(groupedIntegrations));
  }
}

export function* updateIntegrationRequest({ integration }: { integration: Integration }) {
  try {
    yield call(updateIntegration, integration);
    const { data } = yield call(getIntegrations);
    const groupedIntegrations = groupIntegrations(data.results);
    yield put(addPositiveToast('Updated the push credential.'));
    yield put(updateIntegrationResponse(groupedIntegrations));
  } catch (error: any) {
    yield put(updateIntegrationError());
    const msg = extractErrorMessage(error);
    yield put(addNegativeToastWithErrorId(msg, TOAST_ACTIONS.updateIntegration, error));
  }
}

export function* deleteIntegrationRequest({ integration }: { integration: Integration }) {
  try {
    yield call(deleteIntegration, integration);
    const { data } = yield call(getIntegrations);
    const groupedIntegrations = groupIntegrations(data.results);
    yield put(addPositiveToast('Deleted the integration'));
    yield put(deleteIntegrationResponse(groupedIntegrations));
  } catch (error: any) {
    yield put(deleteIntegrationError());
    const msg = extractErrorMessage(error);
    yield put(addNegativeToastWithErrorId(msg, TOAST_ACTIONS.deleteIntegration, error));
  }
}

export function* testPushRequest({
  pushMessage,
}: {
  pushMessage: {
    deviceToken: string;
    message: string;
    pushConfig: Integration;
  };
}) {
  try {
    yield call(sendTestPush, pushMessage);
    yield put(sendTestPushResponse());
    const { data } = yield call(getIntegrations);
    const groupedIntegrations = groupIntegrations(data.results);
    yield put(fetchIntegrationsResponse(groupedIntegrations));
    yield put(
      addPositiveToast(`Notification to '${pushMessage.deviceToken}' was successfully sent.`),
    );
  } catch (error: any) {
    yield put(sendTestPushError());
    const msg = extractErrorMessage(error);
    yield put(
      addNegativeToastWithErrorId(
        `Failed to send notification to '${pushMessage.deviceToken}'. ${msg}.`,
        TOAST_ACTIONS.testPush,
        error,
      ),
    );
  }
}

addToRootSaga([
  // @ts-ignore
  takeEvery(actionTypes.CREATE_MOBILE_APP_REQUEST, createMobileAppRequest),
  // @ts-ignore
  takeEvery(actionTypes.UPDATE_MOBILE_APP_REQUEST, updateMobileAppRequest),
  // @ts-ignore
  takeEvery(actionTypes.DELETE_MOBILE_APP_REQUEST, deleteMobileAppRequest),
  takeLatest(actionTypes.FETCH_MOBILE_APPS_REQUEST, fetchMobileApps),
  // @ts-ignore
  takeLatest(actionTypes.FETCH_MOBILE_APP_REQUEST, fetchMobileAppInfo),
  takeLatest(actionTypes.FETCH_INTEGRATIONS_REQUEST, fetchIntegrationsRequest),
  // @ts-ignore
  takeEvery(actionTypes.CREATE_INTEGRATION_REQUEST, createIntegrationRequest),
  // @ts-ignore
  takeEvery(actionTypes.ASSIGN_INTEGRATION_REQUEST, assignIntegrationRequest),
  // @ts-ignore
  takeEvery(actionTypes.UPDATE_INTEGRATION_REQUEST, updateIntegrationRequest),
  // @ts-ignore
  takeEvery(actionTypes.DELETE_INTEGRATION_REQUEST, deleteIntegrationRequest),
  // @ts-ignore
  takeEvery(actionTypes.SEND_TEST_PUSH_REQUEST, testPushRequest),
  // @ts-ignore
]);
